import { Button, Input, Label, Radio, RadioGroup, TextArea, TextField } from "@heroui/react"
import type { Dsr, DsrPublik } from "@ramoo/contracts"
import { tamu } from "@ramoo/i18n/tamu"
import { useState } from "react"
import { Turnstile } from "./App"
import { Berhasil, JudulPusat, Pusat } from "./bagian"

const teks = tamu()

/**
 * Satu pilihan dari daftar pendek, semua pilihannya langsung terlihat.
 *
 * Padanan `native-select` Pro dengan paket gratis. `Select` gratis tidak dipakai: ia membawa
 * ListBox, Popover, dan mesin seleksinya, sekitar 13 kB gzip untuk dua daftar berisi dua dan lima
 * pilihan, dan itu yang mendorong JS halaman tamu melewati dua kali ukuran semula (DS-8).
 */
function Pilihan<K extends string>({
  label,
  nilai,
  opsi,
  onUbah,
}: {
  label: string
  nilai: K
  opsi: readonly (readonly [K, string])[]
  onUbah: (nilai: K) => void
}) {
  return (
    <RadioGroup value={nilai} onChange={(v) => onUbah(v as K)}>
      <Label>{label}</Label>
      {opsi.map(([kunci, nama]) => (
        <Radio key={kunci} value={kunci}>
          <Radio.Content>
            <Radio.Control>
              <Radio.Indicator />
            </Radio.Control>
            {nama}
          </Radio.Content>
        </Radio>
      ))}
    </RadioGroup>
  )
}

/**
 * Formulir permintaan data pribadi (Modul 13 N6), `/privasi/{kode outlet}`.
 *
 * Kode outlet adalah id outlet yang sama dengan di URL QR meja; organisasinya diturunkan server,
 * tidak pernah dari formulir ini. Dijaga Turnstile kalau site key diisi saat build — tiap
 * permintaan membawa tenggat 3×24 jam yang mengikat, jadi bot tidak boleh bisa membanjirinya.
 * Permintaan staf ditangani Ramoo, bukan toko (K-PDP-3).
 */
export function Privasi({ outletId }: { outletId: string }) {
  const sitekey = import.meta.env.VITE_TURNSTILE_SITEKEY as string | undefined
  const [token, setToken] = useState("")
  const [isi, setIsi] = useState<Omit<DsrPublik, "outletId" | "turnstileToken">>({
    subjectType: "customer",
    subjectId: "",
    type: "access",
    notes: "",
    contactEmail: "",
  })
  const [kirim, setKirim] = useState(false)
  const [galat, setGalat] = useState<string | null>(null)
  const [hasil, setHasil] = useState<Dsr | null>(null)

  const ajukan = async () => {
    setKirim(true)
    setGalat(null)
    try {
      const r = await fetch("/v1/privacy/requests", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ...isi,
          notes: isi.notes?.trim() || undefined,
          contactEmail: isi.contactEmail?.trim() || undefined,
          outletId,
          turnstileToken: token || null,
        } satisfies DsrPublik),
      })
      if (r.status === 404) throw new Error(teks.privasiTokoTakDikenal)
      if (!r.ok) {
        const e = (await r.json().catch(() => null)) as { error?: { message?: string } } | null
        throw new Error(e?.error?.message ?? teks.privasiGagal)
      }
      setHasil((await r.json()) as Dsr)
    } catch (e) {
      setGalat((e as Error).message || teks.privasiGagal)
    } finally {
      setKirim(false)
    }
  }

  if (hasil)
    return (
      <Pusat>
        <Berhasil />
        <JudulPusat>{teks.privasiJudul}</JudulPusat>
        <p className="text-sm">
          {teks.privasiTerkirim(
            new Date(hasil.dueAt).toLocaleString("id-ID", {
              dateStyle: "long",
              timeStyle: "short",
            }),
            hasil.subjectType === "customer" ? "toko" : "ramoo",
          )}
        </p>
      </Pusat>
    )

  return (
    <main className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-6 pb-10">
      <div className="flex flex-col gap-1">
        <JudulPusat>{teks.privasiJudul}</JudulPusat>
        <p className="text-sm text-muted">{teks.privasiSub}</p>
      </div>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          void ajukan()
        }}
      >
        <Pilihan
          label={teks.privasiSiapa}
          nilai={isi.subjectType}
          opsi={(["customer", "staff"] as const).map((x) => [x, teks.privasiSubjek[x] ?? x])}
          onUbah={(subjectType) => setIsi({ ...isi, subjectType })}
        />
        {isi.subjectType === "staff" ? (
          <p className="text-sm text-muted">{teks.privasiStafKeRamoo}</p>
        ) : null}
        <TextField
          fullWidth
          isRequired
          minLength={3}
          maxLength={120}
          value={isi.subjectId}
          onChange={(subjectId) => setIsi({ ...isi, subjectId })}
        >
          <Label>{teks.privasiId}</Label>
          <Input inputMode="tel" autoComplete="tel" />
        </TextField>
        <Pilihan
          label={teks.privasiJenis}
          nilai={isi.type}
          opsi={Object.entries(teks.privasiJenisOpsi) as [DsrPublik["type"], string][]}
          onUbah={(type) => setIsi({ ...isi, type })}
        />
        <TextField
          fullWidth
          maxLength={500}
          value={isi.notes ?? ""}
          onChange={(notes) => setIsi({ ...isi, notes })}
        >
          <Label>{teks.privasiCatatan}</Label>
          <TextArea rows={3} />
        </TextField>
        <TextField
          fullWidth
          type="email"
          maxLength={254}
          value={isi.contactEmail ?? ""}
          onChange={(contactEmail) => setIsi({ ...isi, contactEmail })}
        >
          <Label>{teks.privasiEmail}</Label>
          <Input autoComplete="email" />
          <p className="text-xs text-muted">{teks.privasiEmailHint}</p>
        </TextField>
        {sitekey ? <Turnstile sitekey={sitekey} onToken={setToken} /> : null}
        {galat ? (
          <p className="text-center text-sm text-danger-soft-foreground" role="alert">
            {galat}
          </p>
        ) : null}
        <Button
          fullWidth
          size="lg"
          type="submit"
          isDisabled={kirim || isi.subjectId.trim().length < 3 || (!!sitekey && !token)}
        >
          {kirim ? teks.privasiMengirim : teks.privasiKirim}
        </Button>
      </form>
    </main>
  )
}
